import styled from '@emotion/styled';
import React, { useContext, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { CardLayout } from '../components/CardLayout';
import {
	HistoryIcon,
	LoadingIcon,
	MembershipIcon,
	PaymentIcon,
	ProfileIcon,
	SettingsIcon,
	ShippingIcon
} from '../svg';

import { EmotionBreakpoint, useBrandDetails } from 'dw-au-react-components';
import Fuse from 'fuse.js';
import { SearchDropdown } from '../components/SearchDropdown';
import { FaqContext } from '../context/FaqProvider';

const StyledHelpHeader = styled.div`
	background: #ffffff;
	padding: 60px 0;
	.help-header {
		width: 100%;
		margin: auto;
	}
	.font-28 {
		font-size: 28px;
		letter-spacing: 0.25px;
		line-height: 32px;
	}
`;

const StyledForm = styled.form`
	overflow: initial;
	max-width: 780px;
	input.h52 {
		height: 52px;
	}
	button {
		max-width: 122px;
		width: 100%;
	}
`;

const StyledCardWrapper = styled.div`
	padding: 80px 0;
	.grid-wrapper {
		display: grid;
		gap: 20px;
		grid-template-columns: repeat(3, 1fr);
		${EmotionBreakpoint('mobile')} {
			grid-template-columns: repeat(2, 1fr);
			gap: 15px;
		}
		@media (max-width: 575px) {
			grid-template-columns: auto;
		}
	}
	.card {
		background: #ffffff;
		border: 1px solid #e3e3e3;
		border-radius: 4px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		text-align: center;
		min-height: 240px;
		padding: 40px;
		transition: 0.3s all ease-in-out;
		&:hover {
			text-decoration: none;
			background: #f1f1f1;
		}
		@media (max-width: 575px) {
			padding: 20px;
		}
	}
`;

const StyledHelpBanner = styled.div`
	.phone-btn {
		@media (max-width: 575px) {
			margin-right: 0 !important;
			margin-bottom: 20px;
		}
	}
`;

const StyledProductReviewWrapper = styled.div`
	iframe {
		border: 0;
		overflow: auto;
		min-height: 230px;
		height: auto;
		margin-bottom: 80px;
		@media (min-width: 768px) and (max-width: 991px) {
			min-height: 380px;
		}
		@media (max-width: 767px) {
			min-height: 480px;
			margin-bottom: 40px;
		}
	}
`;

function Help() {
	const { allQuestions } = useContext(FaqContext);
	const [suggestions, setSuggestions] = useState([]);
	const [showDropdown, setShowDropdown] = useState(false);
	const brandData = useBrandDetails();
	const [loader, setLoader] = useState(true);
	const iframeRef = useRef();
	const [height, setHeight] = useState('0px');

	const onLoad = () => {
		setHeight(
			iframeRef.current.contentWindow.document.body.scrollHeight + 'px'
		);
		setLoader(false);
	};

	const handleInputChange = (e) => {
		let query = e.target.value.toLowerCase();

		if (query === '' || query === ' ') {
			setShowDropdown(false);
		} else {
			const options = {
				shouldSort: true,
				threshold: 0.4,
				location: 0,
				distance: 3000,
				maxPatternLength: 32,
				minMatchCharLength: 1,
				keys: [
					{
						name: 'title',
						weight: 0.7
					},
					{
						name: 'answer',
						weight: 0.3
					}
				]
			};

			const fuse = new Fuse(allQuestions, options);
			const getSuggestions = fuse.search(query);
			setSuggestions(getSuggestions.slice(0, 8));
			setShowDropdown(true);
		}
	};

	return (
		<>
			<StyledHelpHeader className={brandData.tag}>
				<Helmet>
					<title>Customer Service - {brandData.name}</title>
				</Helmet>

				<div className='container'>
					<div className='d-flex justify-content-center align-items-center flex-column help-header'>
						<h1
							className={`font-oswald lower mb-30 text-center font-28 ${
								brandData.tag === 'vir' ? 'text-purple-2' : ''
							}`}
						>
							Welcome to {brandData.name} support. We’re here to help!
						</h1>
						<StyledForm className='d-flex justify-content-center form-group mb-0 w-100'>
							<input
								type='search'
								name='search-help'
								id='search-help'
								placeholder='Tell us what you are looking for?'
								className='w-100 noLabel h52'
								onChange={handleInputChange}
								autoComplete='off'
							/>
							<button className='btn btn-red h52 border-radius-0'>
								Search
							</button>
							{showDropdown && (
								<SearchDropdown
									suggestions={suggestions}
									brandData={brandData}
								/>
							)}
						</StyledForm>
					</div>
				</div>
			</StyledHelpHeader>
			<StyledCardWrapper>
				<div className='container'>
					<div className='grid-wrapper'>
						<CardLayout
							linkUrl='/customer-services/faq/wine-plans'
							icon={MembershipIcon}
							heading='My Wine Plans'
							content='Find out more and get the most from your Wine Plan.'
						/>
						<CardLayout
							linkUrl='/customer-services/faq/delivery-and-returns'
							icon={ShippingIcon}
							heading='Delivery and Returns'
							content='Simple, clear answers to your delivery questions.'
						/>
						<CardLayout
							linkUrl='/customer-services/faq/existing-orders'
							icon={HistoryIcon}
							heading='Existing Orders'
							content='Common questions for after you’ve placed your order.'
						/>
						<CardLayout
							linkUrl='/customer-services/faq/payments-and-vouchers'
							icon={PaymentIcon}
							heading='Payment & Vouchers'
							content='Discover how to pay, use a voucher, or get a receipt.'
						/>
						<CardLayout
							linkUrl='/customer-services/faq/order-issues'
							icon={SettingsIcon}
							heading='Order Issues'
							content='Something not quite right? These answers might help.'
						/>
						<CardLayout
							linkUrl='/customer-services/faq/account'
							icon={ProfileIcon}
							heading='My Account'
							content='Your account, password and email questions answered.'
						/>
					</div>
				</div>
			</StyledCardWrapper>
			{brandData.country === 'AU' && (
				<StyledProductReviewWrapper className='container text-center cs_product_review'>
					{loader ? <LoadingIcon extraClassName='overlay' /> : null}
					<iframe
						ref={iframeRef}
						onLoad={onLoad}
						id='password__iframe'
						src={`/jsp/customerservice/au/common/product_review_iframe.jsp?webBrand=${brandData.tag}`}
						height={height}
						width='100%'
					/>
				</StyledProductReviewWrapper>
			)}
			<div className='container'>
				<StyledHelpBanner className='background-off-white-bg border-radius-4 p-40 text-center mb-40'>
					<div
						className={`mb-12 text-${
							brandData.tag === 'vir'
								? 'purple-2'
								: brandData.tag === 'adc'
								? 'adc-blue'
								: 'navyblue'
						}`}
					>
						Haven’t found what you are looking for?
					</div>
					<h3
						className={`lower mb-20 text-${
							brandData.tag === 'vir'
								? 'purple-2'
								: brandData.tag === 'adc'
								? 'adc-blue font-oswald'
								: 'navyblue font-oswald'
						}`}
					>
						Get in contact with our customer service team
					</h3>
					<div className='d-flex justify-content-center align-items-center flex-column flex-sm-row'>
						<a
							className={`btn btn-${
								brandData.tag === 'vir' ? 'purple-2' : 'navyblue'
							}-outline mr-15 phone-btn`}
							href={`tel:${brandData.phone}`}
							target='_blank'
							rel='noreferrer'
						>
							Call {brandData.phone}
						</a>
						<a
							className={`btn btn-${
								brandData.tag === 'vir' ? 'purple-2' : 'navyblue'
							}-outline`}
							href={`mailto:${brandData.emailAddress}`}
							onClick={() => {
								// Adobe DL click Events -> storeui/j2ee/dwint_store.war/assets/components/dataLayer/dist/main.js
								var AdobeDLClickEventObj = {
									clickEvent: 'mailto',
									clickDescription:
										'customer service:mailto:' + brandData.emailAddress,
									name: 'mailto link',
									clickLocation: 'customer service',
									clickText: 'Send an email',
									clickAction: 'mailto'
								};

								// eslint-disable-next-line no-undef
								AdobeDLFunctions.clickEventDL(
									AdobeDLClickEventObj.clickEvent,
									AdobeDLClickEventObj
								);
							}}
							target='_blank'
							rel='noreferrer'
						>
							Send an email
						</a>
					</div>
				</StyledHelpBanner>
			</div>
		</>
	);
}

export default Help;
